import { useMemo, useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { Plus } from 'lucide-react'
import { ConfirmAction } from '@/components/confirm-action'
import { DataTable } from '@/components/data-table'
import { Notice, PageHeader } from '@/components/page'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { localStamp } from '@/lib/datetime'
import { useHasRole } from '@/features/auth/use-session'
import { CoinLabel, SponsoredEditor } from '@/features/sponsored-coins/sponsored-editor'
import {
  HOMEPAGE_SPONSORED_SLOTS,
  useDeleteSponsoredCoin,
  useSetSponsoredState,
  useSponsoredCoins,
  type SponsoredCoin,
} from '@/features/sponsored-coins/api'

/**
 * What the homepage does with the row right now. `live` is the server's answer
 * (its clock); the other three only explain why a row is not live.
 */
function status(row: SponsoredCoin): { label: string; variant: 'success' | 'accent' | 'default' | 'warning' } {
  // `live` is state and window only; the site also needs the coin and its market row.
  if (row.live && !row.coin) return { label: 'Not shown — coin removed', variant: 'warning' }
  if (row.live && row.coin && !row.coin.has_market) return { label: 'Not shown — no market data', variant: 'warning' }
  if (row.duplicate_coin) return { label: 'Not shown — same coin listed above', variant: 'warning' }
  if (row.over_limit) return { label: `Not shown — over the ${HOMEPAGE_SPONSORED_SLOTS}-slot limit`, variant: 'warning' }
  if (row.live) return { label: 'Live now', variant: 'success' }
  if (row.state === 'off') return { label: 'Off', variant: 'default' }
  const now = new Date().toISOString()
  if (row.starts_at && row.starts_at > now) return { label: 'Scheduled', variant: 'accent' }
  return { label: 'Ended', variant: 'default' }
}

function Stamp({ iso, empty }: { iso: string | null; empty: string }) {
  if (!iso) return <span className="text-muted-foreground">{empty}</span>
  const [date, time, zone] = localStamp(iso).split(' ')
  return (
    <span className="whitespace-nowrap">
      {date}{' '}
      <span className="text-faint">
        {time} {zone}
      </span>
    </span>
  )
}

export function SponsoredCoinsPage() {
  const query = useSponsoredCoins()
  const setState = useSetSponsoredState()
  const remove = useDeleteSponsoredCoin()
  const [editing, setEditing] = useState<{ row?: SponsoredCoin } | null>(null)
  const [pendingDelete, setPendingDelete] = useState<SponsoredCoin | null>(null)

  // One after the last row, held at the schema's cap so a new entry's default is valid.
  const nextSortOrder = Math.min(1_000_000, Math.max(0, ...(query.data ?? []).map((row) => row.sort_order)) + 1)
  // The server's writer roles (super_admin, ops_admin); anyone else sees the list only.
  const canWrite = useHasRole('super', 'ops')

  const columns = useMemo<ColumnDef<SponsoredCoin, unknown>[]>(
    () => [
      {
        accessorKey: 'sort_order',
        header: 'Order',
        enableSorting: false,
        cell: ({ row }) => <Badge variant="accent">{row.original.sort_order}</Badge>,
      },
      {
        id: 'coin',
        header: 'Coin',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.coin ? (
            <CoinLabel coin={row.original.coin} />
          ) : (
            <Badge variant="destructive">Coin removed — not shown</Badge>
          ),
      },
      {
        id: 'window',
        header: 'Window',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="space-y-0.5 text-sm">
            <div>
              <span className="text-faint">From </span>
              <Stamp iso={row.original.starts_at} empty="any time" />
            </div>
            <div>
              <span className="text-faint">Until </span>
              <Stamp iso={row.original.ends_at} empty="no expiry" />
            </div>
          </div>
        ),
      },
      {
        id: 'status',
        header: 'On homepage',
        enableSorting: false,
        cell: ({ row }) => {
          const { label, variant } = status(row.original)
          return <Badge variant={variant}>{label}</Badge>
        },
      },
      {
        id: 'state',
        header: 'State',
        enableSorting: false,
        cell: ({ row }) => (
          <Switch
            checked={row.original.state === 'live'}
            disabled={!canWrite || setState.isPending}
            aria-label={`${row.original.state === 'live' ? 'Switch off' : 'Switch on'} ${row.original.coin?.symbol ?? 'this entry'}`}
            onCheckedChange={(checked) =>
              setState.mutate({ id: row.original.id, state: checked ? 'live' : 'off' })
            }
          />
        ),
      },
      {
        id: 'updated',
        header: 'Updated',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="text-sm text-muted-foreground">
            <Stamp iso={row.original.updated_at} empty="—" />
            {row.original.updated_by && <div className="text-xs text-faint">by {row.original.updated_by}</div>}
          </div>
        ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) =>
          canWrite ? (
            <div className="flex justify-end gap-2 whitespace-nowrap">
              <Button variant="secondary" size="sm" onClick={() => setEditing({ row: row.original })}>
                Edit
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setPendingDelete(row.original)}>
                Delete
              </Button>
            </div>
          ) : null,
      },
    ],
    [setState, canWrite],
  )

  return (
    <>
      <PageHeader
        title="Sponsored Coins"
        actions={
          canWrite ? (
            <Button onClick={() => setEditing({})} disabled={query.isPending}>
              <Plus />
              New sponsored coin
            </Button>
          ) : undefined
        }
      />

      <Notice>
        <b>Paid placement above the homepage Top list</b>, on every page of the list, with a{' '}
        <b>Sponsored</b> tag and a tinted row. A coin shows when its <b>State is Live AND now falls
        within its window</b>; the window switches it on and off by itself. The homepage shows{' '}
        <b>at most {HOMEPAGE_SPONSORED_SLOTS}</b>, the first ones in <b>Order</b> (smallest first)
        that it can show, each coin once — a coin with no market data, or listed again below its first
        row, takes no slot. The coin also keeps its normal place in the ranking. Times are shown in
        your local time.
      </Notice>

      {setState.isError && (
        <p className="mb-3 text-xs text-destructive">{(setState.error as Error).message}</p>
      )}

      <Card>
        <DataTable
          columns={columns}
          query={query}
          emptyMessage="No sponsored coins. The homepage shows no sponsored row."
        />
      </Card>

      {/* Keyed so switching rows rebuilds the form with that row's values. */}
      {editing && (
        <SponsoredEditor
          key={editing.row?.id ?? 'new'}
          row={editing.row}
          nextSortOrder={nextSortOrder}
          open
          onOpenChange={(open) => !open && setEditing(null)}
        />
      )}

      <ConfirmAction
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title="Delete this sponsored coin?"
        description={
          <>
            {pendingDelete?.coin
              ? `${pendingDelete.coin.symbol} · ${pendingDelete.coin.name}`
              : 'This entry'}{' '}
            will stop showing on the homepage at once. To pause it instead, switch its State off.
          </>
        }
        confirmLabel="Delete"
        destructive
        onConfirm={() => remove.mutateAsync(pendingDelete!.id)}
        onConflict={() => void query.refetch()}
      />
    </>
  )
}
